/**
 * Punch-holes for the pieces of a page that depend on the logged in
 * account (or, for the brand-categories menu, that vary by account even
 * for reasons unrelated to auth state), so the rest of a page can be
 * rendered once and safely cached/shared across every visitor.
 *
 * Driven off one cheap /commerce-widgets/summary fetch on page load, plus
 * one more for the brand-categories menu:
 *
 *   1. Header cart/wishlist badges - filled in with the real count, or left
 *      showing the guest lock icon.
 *   2. Header cart/wishlist widgets - the real Livewire component (full
 *      dropdown, add/remove/quantity logic) is only ever fetched and
 *      mounted the first time its icon is actually opened, and only for a
 *      logged in account. Livewire v2 has no MutationObserver of its own -
 *      it only discovers components once, at Livewire.start() - so after
 *      injecting server-rendered markup, Livewire.rescan() is called to
 *      register the new component (see ensureWidgetMounted()).
 *   3. [data-reveal-on-auth] / [data-product-price-hole] elements anywhere
 *      on the page (e.g. the featured products carousel, the header's
 *      account/logout links) - revealed once we know the visitor is logged
 *      in, with prices filled in from /commerce-widgets/prices. The inverse,
 *      [data-hide-on-auth] (e.g. the header's login/register links, the
 *      mobile lock icon), stays visible by default and hides once we know
 *      the visitor is actually logged in.
 *   4. The header's brand-categories menu ([data-brand-categories-*-hole])
 *      - fetched and injected for every visitor, guest or logged in, since
 *      it genuinely varies by account (pricelist/country) and can no
 *      longer be server-rendered without breaking caching.
 *   5. The CSRF meta tag and every @csrf hidden field - refreshed from
 *      /commerce-widgets/summary's response regardless of auth state, so
 *      forms on a cached page still submit correctly for whoever's
 *      actually viewing it.
 *   6. The login popup modal ([data-lazy-widget="login-popup"], guests
 *      only) - its own auth/session-dismissed check can't be decided at
 *      page-render time without breaking caching, so it's mounted the same
 *      way as the cart/wishlist widgets, just eagerly instead of on
 *      interaction.
 */
(function () {
    var widgetMountPromises = {};

    function fetchJson(url) {
        return fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })
            .then(function (res) { return res.ok ? res.json() : null; })
            .catch(function () { return null; });
    }

    function widgetRoot(type) {
        return document.querySelector('[data-lazy-widget="' + type + '"]');
    }

    /**
     * Fetches and injects the real Livewire component for a placeholder,
     * idempotently - safe to call multiple times/concurrently for the
     * same widget type.
     */
    function ensureWidgetMounted(type) {
        if (widgetMountPromises[type]) {
            return widgetMountPromises[type];
        }

        var root = widgetRoot(type);
        if (!root) {
            return Promise.resolve(false);
        }

        widgetMountPromises[type] = fetch(root.dataset.widgetUrl, {
            headers: { Accept: 'text/html' },
            credentials: 'same-origin',
        })
            .then(function (res) { return res.ok ? res.text() : null; })
            .then(function (html) {
                if (!html) {
                    delete widgetMountPromises[type];
                    return false;
                }

                root.outerHTML = html;

                // Livewire v2 has no MutationObserver - it only scans the DOM
                // for [wire:initial-data] roots once, at Livewire.start(). A
                // component injected afterwards (like this one) is invisible
                // to it - Livewire.emit() would silently have no listener -
                // until it's explicitly registered via Livewire.rescan().
                Livewire.rescan();

                return true;
            });

        return widgetMountPromises[type];
    }

    function armLazyOpen(root) {
        var type = root.dataset.lazyWidget;
        ['mouseenter', 'focusin', 'click'].forEach(function (evt) {
            root.addEventListener(evt, function () { ensureWidgetMounted(type); });
        });
    }

    function applyAuthenticatedState(authenticated) {
        // Header "please login" copy - only shown once we're sure it's a guest.
        if (!authenticated) {
            document.querySelectorAll('[data-widget-guest-message]').forEach(function (el) {
                el.hidden = false;
            });
        }

        // Any generic "only makes sense for a logged in visitor" element.
        document.querySelectorAll('[data-reveal-on-auth]').forEach(function (el) {
            el.hidden = !authenticated;
        });

        // The inverse: a guest-shaped element that's visible by default (so
        // it's correct for the common cached-page case) and needs hiding
        // once we know the visitor is actually logged in.
        document.querySelectorAll('[data-hide-on-auth]').forEach(function (el) {
            el.hidden = authenticated;
        });

        document.querySelectorAll('[data-strip-on-auth]').forEach(function (el) {
            if (authenticated) {
                el.dataset.stripOnAuth.split(' ').forEach(function (cls) {
                    if (cls) el.classList.remove(cls);
                });
            }
        });
    }

    function applyCounts(cartCount, wishlistCount) {
        document.querySelectorAll('[data-lazy-widget]').forEach(function (root) {
            var badge = root.querySelector('[data-widget-count]');
            if (!badge) return;

            var type = root.dataset.lazyWidget;
            if (type === 'cart' || type === 'cart-mobile') {
                badge.textContent = cartCount;
            } else if (type === 'wishlist' || type === 'wishlist-mobile') {
                badge.textContent = wishlistCount;
            }
        });
    }

    /**
     * Applies a fresh CSRF token to the meta tag and every @csrf hidden
     * field on the page. Needed regardless of auth state: a cached page's
     * baked-in token reflects whichever session rendered the cached copy
     * (the hourly warm job's, for a cache hit), not this visitor's own -
     * without this, the logout/newsletter forms would fail with a 419.
     */
    function refreshCsrfToken(token) {
        if (!token) return;

        var meta = document.querySelector('meta[name="csrf-token"]');
        if (meta) meta.content = token;

        document.querySelectorAll('input[name="_token"]').forEach(function (el) {
            el.value = token;
        });
    }

    /**
     * Punches the header's brand-categories menu in for every visitor -
     * unlike prices/widgets, this isn't gated on being authenticated, since
     * guests need it too (it's no longer server-rendered at all, so the
     * page shell stays identical for everyone and cacheable).
     */
    function loadBrandCategories() {
        // Present at least twice per page (desktop + mobile header), so
        // every match needs filling, not just the first.
        var listHoles = document.querySelectorAll('[data-brand-categories-list-hole]');
        var panelsHoles = document.querySelectorAll('[data-brand-categories-panels-hole]');
        if (!listHoles.length && !panelsHoles.length) return;

        fetchJson('/commerce-widgets/brand-categories').then(function (data) {
            if (!data) return;

            if (data.list !== undefined) {
                listHoles.forEach(function (el) { el.innerHTML = data.list; });
            }
            if (data.panels !== undefined) {
                panelsHoles.forEach(function (el) { el.innerHTML = data.panels; });
            }

            document.dispatchEvent(new CustomEvent('ets:brand-categories-loaded'));
        });
    }

    function loadPrices() {
        var priceHoles = document.querySelectorAll('[data-product-price-hole]');
        var deliverabilityHoles = document.querySelectorAll('[data-product-deliverability-hole]');
        if (!priceHoles.length && !deliverabilityHoles.length) return;

        var ids = {};
        [].slice.call(priceHoles).concat([].slice.call(deliverabilityHoles)).forEach(function (el) {
            ids[el.dataset.productId] = true;
        });

        fetchJson('/commerce-widgets/prices?ids=' + Object.keys(ids).join(',')).then(function (data) {
            if (!data || !data.prices) return;

            priceHoles.forEach(function (el) {
                var info = data.prices[el.dataset.productId];
                if (!info || info.formatted === undefined) return;
                el.textContent = info.formatted;
                el.hidden = false;
            });

            deliverabilityHoles.forEach(function (el) {
                var info = data.prices[el.dataset.productId];
                if (!info) return;
                el.innerHTML = info.deliverability_html;
                el.hidden = false;
            });

            document.querySelectorAll('[data-reveal-on-priced]').forEach(function (el) {
                var info = data.prices[el.dataset.productId];
                if (info && info.formatted !== undefined) {
                    el.hidden = false;
                }
            });
        });
    }

    function init() {
        // Cloudflare Rocket Loader (layout.foot.jsFooter) defers script
        // execution and, to give scripts that missed the real
        // DOMContentLoaded a chance to still run, re-dispatches a synthetic
        // copy of it afterwards - which can mean this fires twice for one
        // real page load. Guard on window (not a module-scoped var) since
        // it also sometimes re-executes the script itself, which would
        // otherwise hand us a second, equally "fresh" closure.
        if (window.__etsCommerceLazyInitialized) return;
        window.__etsCommerceLazyInitialized = true;

        fetchJson('/commerce-widgets/summary').then(function (data) {
            if (!data) return;

            refreshCsrfToken(data.csrf_token);
            applyAuthenticatedState(data.authenticated);
            loadBrandCategories();

            if (!data.authenticated) {
                // Guest-only, and only if app.blade.php actually rendered
                // the placeholder (it won't for an excluded page/domain).
                // A 204 (already dismissed this session) leaves the
                // placeholder as an empty, invisible div - harmless.
                ensureWidgetMounted('login-popup');
            }

            if (data.authenticated) {
                // Only arm the hover/click-to-mount listeners once we know
                // there's something real for them to load - a guest's
                // /commerce-widgets/widget/* would just 404 (the endpoints
                // are login-gated), which is harmless but shows up as a
                // noisy failed-request entry in the browser console for
                // every visitor who so much as hovers the cart icon.
                document.querySelectorAll('[data-lazy-widget]').forEach(armLazyOpen);

                applyCounts(data.cart_count, data.wishlist_count);
                loadPrices();
            }
        });
    }

    // Deferred scripts (see the Rocket Loader note above) can execute after
    // the real DOMContentLoaded already happened, in which case that event
    // will never fire again for a plain listener - so check readyState
    // first and only fall back to listening if the document is still
    // actually loading.
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init);
    } else {
        init();
    }

    window.EtsCommerceLazy = {
        addToCart: function (productId) {
            ensureWidgetMounted('cart').then(function () {
                Livewire.emit('addToCart', productId);
            });
        },
        addToWishlist: function (productId) {
            ensureWidgetMounted('wishlist').then(function () {
                Livewire.emit('addToWishlist', productId);
            });
        },
        quickView: function (productId) {
            ensureWidgetMounted('product-modal').then(function () {
                Livewire.emit('updateModalProduct', productId);
            });
        },
    };
})();
